import { createListenerMiddleware, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ChartType } from 'components/query-charts/query-charts.types';
import { saveStorageValue } from 'hooks/use-storage/use-storage.utils';
import { getStorageValuesWithPrefix } from 'utils/storage';
import { RootState } from './root.types';

export interface ChartState {
  chartType: ChartType;
  xAxisField: string | null;
  yAxisField: string[];
  error: string;
  isLoading: boolean;
}

export const queryChartStorageKey = 'sb:query-charts';

const getInitialState = () => {
  const chartOptions = getStorageValuesWithPrefix(queryChartStorageKey);
  return chartOptions.reduce<ChartState>(
    (initialState, { value }) => {
      initialState.chartType = value?.chartType;
      initialState.xAxisField = value?.xAxisField;
      initialState.yAxisField = value?.yAxisField;
      initialState.error = '';
      initialState.isLoading = false;
      return initialState;
    },
    {
      chartType: 'xy',
      xAxisField: null,
      yAxisField: [],
      error: '',
      isLoading: false,
    }
  );
};

export const queryChartSliceName = 'queryChart';

export const queryChartSlice = createSlice({
  name: queryChartSliceName,
  initialState: getInitialState(),
  reducers: {
    setChartType: (state, action: PayloadAction<ChartType>) => {
      state.chartType = action.payload;
    },
    setXAxisField: (state, action: PayloadAction<string | null>) => {
      state.xAxisField = action.payload;
    },
    setYAxisField: (state, action: PayloadAction<string[]>) => {
      state.yAxisField = [...action.payload];
    },
    setChartError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const { setChartType, setXAxisField, setYAxisField, setChartError, setLoading } = queryChartSlice.actions;

export const queryChartMiddleware = createListenerMiddleware();

queryChartMiddleware.startListening({
  matcher: isAnyOf(setChartType, setXAxisField, setYAxisField),
  effect: (action, listenerApi) => {
    const queryChartState = (listenerApi.getState() as RootState)[queryChartSliceName];

    saveStorageValue({
      storageKey: queryChartStorageKey,
      value: { chartType: queryChartState.chartType, xAxisField: queryChartState.xAxisField, yAxisField: queryChartState.yAxisField },
    });
  },
});

export default queryChartSlice.reducer;
